import { useModalStatusMessage } from '@/hooks';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useMutation } from '@apollo/client';
import UPDATE_DEVICE_USER_RELATIONS from '@features/access/graphql/mutations/updateDeviceUserRelations';
import { Access } from '@features/authorization';
import { DeviceUserValue, UsersAccessForDeviceInput } from '@features/devicesAccessInputs';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Typography } from '@mui/material';
import { useEffect, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import GET_ALL_DEVICE_ACCESS_FOR_DEVICE from '../allAccessTab/graphql/queries/getAllDeviceAccessForDevice';
import { TabContent, TabProps } from '../common';

type FormValues = {
  deviceId: string;
  updatedRelations: DeviceUserValue[];
};

export const DeviceAccessTab: FC<TabProps> = ({ deviceId, onClose, setIsDirty }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const { setModalState: setAddUsersModalState } = useGlobalModal('addDeviceUserRelationsModal');
  const { setModalState: setRemoveUsersModalState } = useGlobalModal('removeDeviceUserRelationsModal');

  const [updateRelations] = useMutation(UPDATE_DEVICE_USER_RELATIONS, {
    // AMARU-609 (mirror AMARU-562): el tab "Todos los accesos" agrega este mismo dato — sin esto
    // queda desactualizado tras guardar acá, aunque el usuario no lo tenga montado en este momento.
    refetchQueries: [{ query: GET_ALL_DEVICE_ACCESS_FOR_DEVICE, variables: { deviceId } }],
    // AMARU-599: esperar el refetch antes de dar el guardado por exitoso. Sin esto el snackbar
    // de exito sale igual cuando el refetch falla, y el tab "Todos los accesos" queda stale
    // sin que nadie se entere.
    awaitRefetchQueries: true,
    onCompleted() {
      methods.reset();
      openModalSuccessMessage(_(msg`Relaciones actualizadas correctamente`));
    },
    onError(error) {
      openModalErrorMessage(error.message);
    },
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      deviceId,
      updatedRelations: [],
    },
  });

  useEffect(() => setIsDirty(methods.formState.isDirty), [methods.formState.isDirty, setIsDirty]);

  const onSubmit = methods.handleSubmit((data) => {
    updateRelations({
      variables: {
        inputs: data.updatedRelations,
      },
    });
  });

  const onAddMoreUsersButtonClick = () => {
    setAddUsersModalState({
      open: true,
      fixedDeviceId: deviceId,
    });
  };

  const onRemoveUsersButtonClick = () => {
    setRemoveUsersModalState({
      open: true,
      fixedDeviceId: deviceId,
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <TabContent
        slotProps={{
          modalActions: {
            onClose,
            onSubmit,
            onResetForm: () => methods.reset(),
            submitLoading: methods.formState.isSubmitting,
            dirtyForm: methods.formState.isDirty,
            customButtons: [
              {
                onClick: onAddMoreUsersButtonClick,
                label: _(msg`Agregar más usuarios`),
                authorization: {
                  access: Access.CreateDeviceUserAccess,
                },
              },
              {
                onClick: onRemoveUsersButtonClick,
                label: _(msg`Eliminar registros`),
                color: 'error',
                authorization: {
                  access: Access.DeleteDeviceUserAccess,
                },
              },
            ],
          },
        }}
      >
        <Typography variant="h6" mb={2}>
          <Trans>Usuarios con acceso directo al dispositivo</Trans>
        </Typography>
        <Controller
          control={methods.control}
          name="updatedRelations"
          render={({ field: { ref, ...props } }) => (
            <UsersAccessForDeviceInput height={380} deviceId={deviceId} {...props} />
          )}
        />
      </TabContent>
    </form>
  );
};
